import { createContext, useContext, useEffect, useState } from 'react';
import { CARD_RADIUS } from './theme-tokens.js';
import { INOVIU_MARK_WHITE } from './inoviu-mark.js';
import MicrosoftSignInButton from './components/MicrosoftSignInButton.jsx';

// AuthGate — DECIDE WHETHER TO RENDER THE APP OR ASK FOR A SIGN-IN.
//
// The server already refuses every API route without a session, so this changes
// nothing about SECURITY — a determined visitor can read this file. What it
// changes is what a signed-out person SEES: without it the shell loads, fires a
// dozen fetches, and paints a screen of failed panels. That reads as "the app is
// broken", not as "you are not signed in".
//
// ⚠ HOW IT KNOWS AUTH IS EVEN ON, without a second flag to keep in sync:
// /auth/me is only mounted when the four ENTRA_* values are present. So the
// ROUTE ITSELF is the signal —
//     not JSON     auth is off; this is Alex's machine, render the app
//     signedIn:f   auth is on and nobody is signed in → ask
//     signedIn:t   render the app
//
// ⚠ 'NOT JSON', NOT '404'. This comment used to say 404, and that was wrong:
// the SPA catch-all answers anything outside /api/ with index.html and HTTP
// 200, so an unmounted /auth/me returns the shell, not a 404. The behaviour was
// right by accident — r.json() throws on HTML and the catch below treats it as
// disabled — but a comment describing a status code that never arrives is how
// the next person 'fixes' this into something that breaks. The same catch-all
// is why the Render health check points at /healthz and not here.
// A separate window.__AUTH_ENABLED__ would be a second source of truth that could
// disagree with the server, and it would disagree in the direction that shows a
// sign-in screen nobody can satisfy.
//
// A network failure renders the app rather than the sign-in screen: the API calls
// will fail visibly and honestly, which beats a sign-in button that cannot work
// because the server is down.
// WHO IS SIGNED IN, for the parts of the UI that must look different per group.
//
// ⚠ THIS IS PRESENTATION ONLY, AND NOTHING HERE IS A CONTROL. Every route is
// already gated server-side by auth-guard.js; a group read from this context can
// be edited in a debugger and gains nobody anything. Its job is to stop showing
// people doors they cannot open.
//
// group is 'internal' when auth is OFF, so Alex's machine behaves exactly as it
// always has rather than greying out his own tools.
export const AuthContext = createContext({ enabled: false, signedIn: false, group: 'internal', who: null, projectId: null });
export const useAuthGroup = () => useContext(AuthContext).group;
// useAuth() — the whole context, for the one control that needs more than the group.
// `who` is the display name (or email), carried so a sign-out control can say WHOSE
// session it is about to end. /auth/me has always returned name + email; this
// context simply threw them away until there was something to show them in.
export const useAuth = () => useContext(AuthContext);

export default function AuthGate({ children }) {
  const [state, setState] = useState({ status: 'checking' });

  useEffect(() => {
    let alive = true;
    fetch('/auth/me')
      .then(async (r) => {
        if (!alive) return;
        if (!r.ok) return setState({ status: 'disabled' });
        // Explicit rather than relying on json() throwing: the catch-all serves
        // HTML with a 200 when auth is not mounted.
        if (!(r.headers.get('content-type') || '').includes('application/json')) {
          return setState({ status: 'disabled' });
        }
        const me = await r.json();
        setState(me.signedIn ? { status: 'in', me } : { status: 'out' });
      })
      .catch(() => { if (alive) setState({ status: 'disabled' }); });
    return () => { alive = false; };
  }, []);

  // Nothing at all while checking — a flash of "sign in" that vanishes is worse
  // than a blank moment, because it makes a signed-in user reach for the button.
  if (state.status === 'checking') return null;
  if (state.status === 'in' || state.status === 'disabled') {
    const value = state.status === 'disabled'
      // Single-user mode: there is no session, so there is nothing to sign out OF.
      // `who: null` is what the header reads to decide not to offer it.
      ? { enabled: false, signedIn: false, group: 'internal', who: null, projectId: null }
      : {
        enabled: true,
        signedIn: true,
        group: state.me?.group || 'customer',
        who: state.me?.name || state.me?.email || 'Signed in',
        // A `project` login's one project — App locks the Working-in selector to it.
        projectId: state.me?.project_id ?? null,
      };
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
  }

  // returnTo is round-tripped so a deep link survives the sign-in. The server
  // rejects anything that is not a same-origin relative path, so this cannot be
  // turned into an open redirect from here.
  const returnTo = window.location.pathname + window.location.search;
  const href = `/auth/login?returnTo=${encodeURIComponent(returnTo)}`;

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--th-bg)', padding: 24,
    }}>
      <div style={{
        maxWidth: 460, width: '100%', background: 'var(--th-row-even)',
        borderRadius: CARD_RADIUS, border: '1px solid var(--th-border-subtle)', overflow: 'hidden',
      }}>
        <div style={{
          background: 'var(--th-bar-bg)', color: 'var(--th-bar-text)', padding: '14px 20px',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <img src={INOVIU_MARK_WHITE} alt="Inoviu" style={{ height: 16, width: 'auto', filter: 'var(--th-mark-filter)' }} />
          <span style={{ fontWeight: 700, fontSize: 'var(--fs-title)', letterSpacing: 0.5 }}>Inoviu</span>
        </div>
        <div style={{ padding: '22px 20px 24px' }}>
          <h1 style={{ margin: '0 0 8px', fontSize: 'var(--fs-title)', color: 'var(--th-text-body)' }}>
            Sign in to continue
          </h1>
          <p style={{ margin: '0 0 18px', color: 'var(--th-text-muted)', fontSize: 'var(--fs-small)', lineHeight: 1.5 }}>
            This catalog is private. Sign in with the Microsoft account you were invited with.
          </p>
          <MicrosoftSignInButton href={href} />
          <p style={{ margin: '18px 0 0', color: 'var(--th-text-muted)', fontSize: 'var(--fs-small)', lineHeight: 1.5 }}>
            Signing in is not the same as being given access. If you have not been
            added to a project yet, you will be told so after signing in.
          </p>
        </div>
      </div>
    </div>
  );
}
